<template>
	<div class="input" :class="{ 'field' : label }" @dblclick="$emit('dblclick', $event)">
		<div v-if="label" class="label">
			<label :for="id">{{ label }}</label>
		</div>
		<ElInput
			:id="id"
			v-model="internalValue"
			v-bind="$attrs"
			:type="type"
			:autocomplete="autocomplete"
			:class="{ 'c-editor' : label }"
			@change="$emit('change', $event)"
			@input="handleInput"
			@focus="$emit('focus', $event)"
			@blur="$emit('blur', $event)"
		>
			<template v-for="(_, name) in $slots" #[name]>
				<slot :name="name" />
			</template>
		</ElInput>
		<div v-if="error" class="c-validation block" :class="{'c-validation--box': validationIsBox}">
			{{ error }}
		</div>
		<span v-if="$slots['help-text']" class="input__help-text"><slot name="help-text"></slot></span>
	</div>
</template>

<script lang="ts">
	import { Input } from '@cognitoforms/element-ui';
	import { defineComponent, ref, type PropType } from 'vue';

	export type InputType = 'text' | 'textarea' | 'search' | 'email' | 'password';

	export default defineComponent({
		name: 'Input',
		components: {
			ElInput: Input
		},
		props: {
			id: {
				type: String,
				required: true
			},
			label: {
				type: String,
				default: ''
			},
			error: {
				type: String,
				default: null
			},
			type: {
				type: String as PropType<InputType>,
				default: 'text'
			},
			value: {
				type: String,
				default: ''
			},
			autocomplete: {
				type: String as PropType<'on' | 'off'>,
				default: 'off'
			},
			disallowedCharacters: {
				type: RegExp,
				default: null
			},
			validationIsBox: {
				type: Boolean,
				default: false
			}
		},
		emits: ['input', 'change', 'focus', 'blur', 'dblclick'],
		setup(props) {
			return {
				internalValue: ref(props.value)
			};
		},
		watch: {
			value(val) {
				this.internalValue = val;
			}
		},
		methods: {
			handleInput() {
				if (this.disallowedCharacters)
					this.internalValue = this.internalValue.replace(this.disallowedCharacters, '');

				this.$emit('input', this.internalValue);
			},
			focus() {
				this.$el.querySelector('input').focus();
			}
		}
	});
</script>

<style lang="scss" src="@cognitoforms/styles/app/input.scss"></style>

<style lang="scss">
.input {

	input:disabled {
		background: var(--bg-disabled);
		cursor: not-allowed;
	}

	:root & label {
		// override a weird legacy style
		cursor: default;
	}
}
</style>